import { TRANSITION_DELAY } from '@/constants/register.contants'
import { useHubRegistration } from '@/data-hooks/mutations/Landing/useHubRegistration'
import { useGetMotivations } from '@/data-hooks/queries/Landing/useGetMotivations'
import { useCallback, useState, useEffect } from 'react'

export const useRegister = (): UseRegisterReturn => {
  // Fetch motivations data
  const { data: motivationsData } = useGetMotivations()

  // State management
  const [userInfo, setUserInfo] = useState<UserInfo>({
    name: '',
    email: '',
    organization: '',
  })

  const [ackInfo, setAckInfo] = useState<AcknowledgementInfo>({
    has_given_consent: false,
    has_agreed_to_receive_emails: false,
    has_given_permission_to_process_data: false,
    has_acknowledged_for_demo_showcases: false,
  })

  // Initialize interests with an empty array, will be updated when motivationsData is available
  const [interests, setInterests] = useState<Interest[]>([])
  const [additionalThoughts, setAdditionalThoughts] = useState('')
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])

  // Animation control states
  const [showWelcome, setShowWelcome] = useState(false)
  const [showUserForm, setShowUserForm] = useState(false)
  const [showUserInfo, setShowUserInfo] = useState(false)
  const [showInterestMessage, setShowInterestMessage] = useState(false)
  const [showInterestForm, setShowInterestForm] = useState(false)
  const [showInterestInfo, setShowInterestInfo] = useState(false)
  const [showConsentMessage, setShowConsentMessage] = useState(false)
  const [showConsentForm, setShowConsentForm] = useState(false)
  const [showThankYou, setShowThankYou] = useState(false)
  const [showError, setShowError] = useState(false)

  // Set interests when motivationsData changes
  useEffect(() => {
    if (motivationsData) {
      // Only update if interests are empty or reset was called
      if (interests.length === 0) {
        setInterests(motivationsData.map((motivation) => ({ ...motivation, selected: false })))
      }
    }
  }, [motivationsData, interests.length])

  const { mutation: hubRegistration } = useHubRegistration({
    handleOnSuccess: (res) => {
      setShowThankYou(true)
      console.log('first step done', res)
    },
    handleOnError: (err) => {
      setShowThankYou(false)
      setShowError(true)
      console.log('first step failed', err)
    },
  })

  // Log store for submission data
  const logSubmission = useCallback(
    (userInfo: UserInfo, interests: string[], thoughts: string, acknowledgements: AcknowledgementInfo) => {
      hubRegistration.mutate({
        userInfo,
        interests,
        additionalThoughts: thoughts,
        acknowledgementInfo: acknowledgements,
      })

      console.log('Form submitted:', { userInfo, interests, thoughts, acknowledgements })
    },
    [],
  )

  // Reset all form states
  const resetFormStates = useCallback(() => {
    setUserInfo({ name: '', email: '', organization: '' })
    setInterests([]) // Reset to empty array which will trigger the useEffect
    setSelectedInterests([])
    setAdditionalThoughts('')

    // Reset animation states
    setShowWelcome(false)
    setShowUserForm(false)
    setShowUserInfo(false)
    setShowInterestMessage(false)
    setShowInterestForm(false)
    setShowInterestInfo(false)
    setShowConsentMessage(false)
    setShowConsentForm(false)
    setShowThankYou(false)
    setShowError(false)
  }, [])

  // Initialize the form sequence
  const initializeForm = useCallback(() => {
    resetFormStates()

    // Start the animation sequence
    setTimeout(() => {
      setShowWelcome(true)
    }, TRANSITION_DELAY)
  }, [resetFormStates])

  // Form submission handlers
  const handleUserFormSubmit = useCallback((values: UserInfo) => {
    setUserInfo(values)
    setShowUserForm(false)

    // Show user info after a brief delay
    setTimeout(() => {
      setShowUserInfo(true)
    }, TRANSITION_DELAY)
  }, [])

  const handleInterestFormSubmit = useCallback(
    (selectedInterests: Interest[], thoughts: string) => {
      setInterests(selectedInterests)
      setAdditionalThoughts(thoughts)
      setShowInterestForm(false)

      // Show user info after a brief delay
      setTimeout(() => {
        setShowInterestInfo(true)
      }, TRANSITION_DELAY)
    },
    [userInfo],
  )

  const handleConsentFormSubmit = useCallback(
    (selectedInterests: string[], thoughts: string, ackInfo: AcknowledgementInfo) => {
      setShowConsentMessage(false)
      setShowConsentForm(false)
      // Log the final submission
      logSubmission(userInfo, selectedInterests, thoughts, ackInfo)
    },
    [userInfo, logSubmission],
  )

  // Animation sequence handlers
  const handleWelcomeComplete = useCallback(() => {
    setShowUserForm(true)
  }, [])

  const handleUserInfoComplete = useCallback(() => {
    setShowInterestMessage(true)
  }, [])

  const handleInterestMessageComplete = useCallback(() => {
    setShowInterestForm(true)
  }, [])

  const handleInterestInfoComplete = useCallback(() => {
    setShowConsentMessage(true)
  }, [])

  const handleConsentMessageComplete = useCallback(() => {
    setShowConsentForm(true)
  }, [])

  return {
    // State
    userInfo,
    interests,
    selectedInterests,
    additionalThoughts,
    ackInfo,

    // Animation control states
    showWelcome,
    showUserForm,
    showUserInfo,
    showInterestMessage,
    showInterestForm,
    showInterestInfo,
    showConsentMessage,
    showConsentForm,
    showThankYou,
    showError,

    // Handlers
    handleUserFormSubmit,
    handleInterestFormSubmit,
    handleConsentFormSubmit,
    handleWelcomeComplete,
    handleUserInfoComplete,
    handleInterestMessageComplete,
    handleInterestInfoComplete,
    handleConsentMessageComplete,
    resetFormStates,
    initializeForm,
    setShowConsentMessage,
    setShowError,
    setAckInfo,
    setSelectedInterests,
  }
}
